import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import DocumentToolbar from './DocumentToolbar';
import api from '../../services/api';

/**
 * Delivery order preview.
 *
 * This page used to redraw the order in HTML while the toolbar downloaded a
 * PDF the server drew separately — the same split that had the arrival notice
 * showing one document on screen and printing another, removed on 2026-09-16.
 *
 * On a multi-house shipment the HTML version was not merely different, it was
 * wrong: it took `hbls[0]` for the letterhead, trucker and delivery address,
 * then summed package count, weight and CBM across EVERY house. OI-26080224
 * would have printed SHSC26080033's consignee and door over both houses'
 * cargo. The server refuses that case outright — `narrowToHouseBill` will not
 * scope a shipment whose containers are not linked to a house, and returns a
 * 400 naming the houses instead of guessing — and refusing beats printing,
 * because a driver sent for the wrong box is not recoverable.
 *
 * So the preview IS the PDF now, and `?hbl_id=` names the house. It is passed
 * by the house bill's own Tools menu, so the operator never picks from a list
 * (operator 2026-09-16: 每次Fay点AN就会只生产这个HBL的AN，而不用选择要哪个HBL了).
 */

function useShipmentType() {
  const { pathname } = useLocation();
  if (pathname.includes('/air-import/')) return 'air-import';
  if (pathname.includes('/ocean-import/')) return 'ocean-import';
  if (pathname.includes('/ocean-export/')) return 'ocean-export';
  if (pathname.includes('/misc/')) return 'misc';
  return 'truck';
}

const DeliveryOrder = () => {
  const { id } = useParams();
  const shipmentType = useShipmentType();
  const [searchParams] = useSearchParams();
  const wantedHbl = searchParams.get('hbl_id') || '';

  const [shipment, setShipment] = useState(null);
  const [houses, setHouses] = useState([]);
  const [hblId, setHblId] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/${shipmentType}/${id}`);
        if (cancelled) return;
        const raw = r.data || {};
        const hs = raw.hbls || raw.hawbs || [];
        setShipment(raw);
        setHouses(hs);
        const asked = hs.find((h) => String(h.id) === String(wantedHbl));
        if (asked) setHblId(String(asked.id));
        else if (hs.length > 1) setHblId(String(hs[0].id));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load shipment');
      }
    })();
    return () => { cancelled = true; };
  }, [id, shipmentType, wantedHbl]);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (hblId) p.set('hbl_id', hblId);
    return p.toString();
  }, [hblId]);

  const qs = params();
  const docPath = `/pdf/shipment/${shipmentType}/${id}/delivery-order`;
  const pdfUrlForToolbar = qs ? `${docPath}?${qs}` : docPath;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api.get(pdfUrlForToolbar, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(
          new Blob([r.data], { type: 'application/pdf' }));
        setPdfUrl(objectUrl);
      } catch (e) {
        if (cancelled) return;
        // The server says WHY it refused — "no container is assigned to a
        // house bill", "two house bills going to different places" — and that
        // sentence is the fix, so it has to reach the operator rather than
        // being flattened into "failed to render".
        let msg = 'Failed to render the delivery order';
        const body = e.response?.data;
        if (body instanceof Blob) {
          try { msg = JSON.parse(await body.text()).error || msg; } catch { /* keep */ }
        } else if (body?.error) {
          msg = body.error;
        }
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) setTimeout(() => window.URL.revokeObjectURL(objectUrl), 5000);
    };
  }, [pdfUrlForToolbar]);

  const house = houses.find((h) => String(h.id) === String(hblId));
  const houseNo = house ? (house.hbl_no || house.hawb_no || `#${house.id}`) : '';
  const fileName = `DO-${(shipment && shipment.file_no) || id}`
    + `${houseNo ? `-${houseNo}` : ''}.pdf`;

  return (
    <>
      <DocumentToolbar
        title="Delivery Order"
        pdfUrl={pdfUrlForToolbar}
        fileName={fileName}
        onClose={() => window.close()}
      />

      {houses.length > 1 && (
        <div className="no-print" style={{
          display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
          padding: '10px 20px', background: '#f1f5f9',
          borderBottom: '1px solid #e2e8f0', fontSize: 13, color: '#334155',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>House B/L</span>
            <select value={hblId} onChange={(e) => setHblId(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 6,
                             border: '1px solid #cbd5e1', fontSize: 13 }}>
              {houses.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.hbl_no || h.hawb_no || `#${h.id}`}
                  {h.trucker ? ` — ${h.trucker}` : ''}
                </option>
              ))}
            </select>
            <span style={{ color: '#64748b' }}>
              one house bill is one delivery order
            </span>
          </label>
        </div>
      )}

      {error && (
        <div style={{ padding: 24, color: '#b91c1c', lineHeight: 1.6 }}>{error}</div>
      )}
      {loading && !error && (
        <div style={{ padding: 24, color: '#64748b' }}>Rendering…</div>
      )}
      {pdfUrl && !error && (
        <iframe
          title="Delivery Order"
          src={pdfUrl}
          style={{ width: '100%', height: 'calc(100vh - 110px)', border: 'none' }}
        />
      )}
    </>
  );
};

export default DeliveryOrder;
